/**
 * PipelineExportHistoryDialog — recent-exports modal.
 *
 * Shows the most recent pipeline exports.
 *  - Contractors: own history (GET /api/exports/pursuits/my-history)
 *  - Consultants: own + their org's exports (GET .../audit-log)
 *  - Super-admins: every export across the platform (GET .../audit-log)
 *
 * Requirement #9 — v3.16.0.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { FileText, FileSpreadsheet, FileCode2, History } from 'lucide-react';
import { useAuth, ROLES } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORMAT_ICONS = {
  pdf: { icon: FileText, color: 'text-red-500' },
  xlsx: { icon: FileSpreadsheet, color: 'text-emerald-600' },
  csv: { icon: FileCode2, color: 'text-slate-600' },
};

export default function PipelineExportHistoryDialog({ open, onOpenChange }) {
  const { user, getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState('my'); // 'my' | 'org' | 'all'

  const canSeeOrgWide =
    user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.CONSULTANT;

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        scope === 'my'
          ? '/api/exports/pursuits/my-history'
          : '/api/exports/pursuits/audit-log';
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setEvents(body.events || []);
    } catch (e) {
      console.error('Pipeline export history fetch failed', e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [scope, getAuthHeaders]);

  useEffect(() => {
    if (open) fetchEvents();
  }, [open, fetchEvents]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="pipeline-export-history-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            Recent Pipeline Exports
          </DialogTitle>
          <DialogDescription>
            Audit log of pipeline downloads — useful for tracking
            who pulled what data and when.
          </DialogDescription>
        </DialogHeader>

        {/* Scope tabs (only for users who can see beyond their own history) */}
        {canSeeOrgWide && (
          <div className="flex gap-2 border-b border-slate-200 -mt-2 pb-2">
            <button
              className={`text-xs px-3 py-1 rounded-md transition ${
                scope === 'my'
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              onClick={() => setScope('my')}
              data-testid="pipeline-export-history-scope-my"
            >
              My exports
            </button>
            <button
              className={`text-xs px-3 py-1 rounded-md transition ${
                scope === 'org'
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              onClick={() => setScope('org')}
              data-testid="pipeline-export-history-scope-org"
            >
              {user?.role === ROLES.SUPER_ADMIN ? 'All exports' : 'My org'}
            </button>
          </div>
        )}

        <div className="max-h-96 overflow-y-auto space-y-2">
          {loading ? (
            <>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </>
          ) : events.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">
              No pipeline exports yet.
            </div>
          ) : (
            events.map((e, idx) => {
              const f = FORMAT_ICONS[e.format] || FORMAT_ICONS.csv;
              const Icon = f.icon;
              const kb = e.file_size ? (e.file_size / 1024).toFixed(1) : '—';
              return (
                <div
                  key={e.id || idx}
                  className="flex items-center gap-3 p-3 border border-slate-200 rounded-md hover:border-slate-300"
                  data-testid={`pipeline-export-history-row-${idx}`}
                >
                  <Icon className={`w-5 h-5 ${f.color} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="uppercase text-[10px]">
                        {e.format}
                      </Badge>
                      <span className="text-sm font-medium text-slate-900">
                        {e.row_count?.toLocaleString() || '0'} rows
                      </span>
                      <span className="text-xs text-slate-500">· {kb} KB</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      <span className="font-medium">By:</span>{' '}
                      {e.user_email || '—'}{' '}
                      <span className="ml-2">
                        {e.created_at
                          ? new Date(e.created_at).toLocaleString()
                          : '—'}
                      </span>
                    </div>
                    {e.filter_summary && e.filter_summary !== 'All pursuits' && (
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                        Filters: {e.filter_summary}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
